import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getBillingConfigApi, saveBillingConfigApi } from '../../apis/index';
import { IBillingConfig } from '../../../schemas/apis/interface';

// Async thunks
export const fetchBillingConfig = createAsyncThunk(
  'billing/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getBillingConfigApi();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch billing configuration'
      );
    }
  }
);

export const saveBillingConfig = createAsyncThunk(
  'billing/saveConfig',
  async (config: IBillingConfig, { rejectWithValue }) => {
    try {
      const response = await saveBillingConfigApi(config);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to save billing configuration'
      );
    }
  }
);

interface BillingState {
  config: IBillingConfig | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  lastSaved: string | null;
  isConfigLoaded: boolean;
}

const initialState: BillingState = {
  config: null,
  loading: false,
  saving: false,
  error: null,
  lastSaved: null,
  isConfigLoaded: false,
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    updateBillingConfig: (
      state,
      action: PayloadAction<Partial<IBillingConfig>>
    ) => {
      if (state.config) {
        state.config = {
          ...state.config,
          ...action.payload,
        };
      } else {
        state.config = action.payload as IBillingConfig;
      }
    },
    updateCompanyDetails: (
      state,
      action: PayloadAction<Partial<IBillingConfig['companyDetails']>>
    ) => {
      if (state.config) {
        state.config.companyDetails = {
          ...state.config.companyDetails,
          ...action.payload,
        };
      }
    },
    updateAddress: (
      state,
      action: PayloadAction<Partial<IBillingConfig['address']>>
    ) => {
      if (state.config) {
        state.config.address = {
          ...state.config.address,
          ...action.payload,
        };
      }
    },
    updateGst: (
      state,
      action: PayloadAction<Partial<IBillingConfig['gst']>>
    ) => {
      if (state.config) {
        state.config.gst = {
          ...state.config.gst,
          ...action.payload,
        } as IBillingConfig['gst'];
      }
    },
    updateCallCharges: (
      state,
      action: PayloadAction<Partial<IBillingConfig['callCharges']>>
    ) => {
      if (state.config) {
        state.config.callCharges = {
          ...state.config.callCharges,
          ...action.payload,
        } as IBillingConfig['callCharges'];
      }
    },
    updateTelephony: (
      state,
      action: PayloadAction<Partial<IBillingConfig['telephony']>>
    ) => {
      if (state.config) {
        state.config.telephony = {
          ...state.config.telephony,
          ...action.payload,
        } as IBillingConfig['telephony'];
      }
    },
    updateCallForwarding: (
      state,
      action: PayloadAction<Partial<IBillingConfig['callForwarding']>>
    ) => {
      if (state.config) {
        state.config.callForwarding = {
          ...state.config.callForwarding,
          ...action.payload,
        } as IBillingConfig['callForwarding'];
      }
    },
    clearBillingError: state => {
      state.error = null;
    },
    resetBillingState: state => {
      state.config = null;
      state.loading = false;
      state.saving = false;
      state.error = null;
      state.lastSaved = null;
      state.isConfigLoaded = false;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch billing config
      .addCase(fetchBillingConfig.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillingConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.config = action.payload.data || null;
        state.isConfigLoaded = true;
        state.error = null;
      })
      .addCase(fetchBillingConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Don't set isConfigLoaded = true on rejection to allow retry after login
        state.isConfigLoaded = false;
      })
      // Save billing config
      .addCase(saveBillingConfig.pending, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveBillingConfig.fulfilled, (state, action) => {
        state.saving = false;
        state.config = action.payload.data;
        state.lastSaved = new Date().toISOString();
        state.error = null;
      })
      .addCase(saveBillingConfig.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  updateBillingConfig,
  updateCompanyDetails,
  updateAddress,
  updateGst,
  updateCallCharges,
  updateTelephony,
  updateCallForwarding,
  clearBillingError,
  resetBillingState,
} = billingSlice.actions;

export default billingSlice.reducer;
